import { Cabinet } from 'lib/modelBuilder/entities/Cabinet';
import { Shelf } from 'lib/modelBuilder/entities/Shelf';
import { call, put, select, take, race } from '@redux-saga/core/effects';
import { makeSelectShelfSelectionProfile } from 'containers/Ribbon/ShelvesRibbon/selectors';
import { updateProfileSelectionAction } from 'containers/Ribbon/ShelvesRibbon/actions';
import { selectionHandlesAction } from 'containers/HandlesPanel/actions';
import { makeSelectGetSetCompositionSelected } from 'containers/HandlesPanel/selectors';
import { PositionAngle, ShelfComposition } from 'lib/modelBuilder/constants';
import _ from 'lodash';
import { makeSelectDrawerSelectedNumber } from 'containers/DrawersCompositionPanel/selectors';
import ActionTypesGeneral, {
  ActivePanel,
} from 'containers/Configurator/constants/general';
import {
  makeSelectShelvesLevelSelected,
  makeSelectShelvesSelectedLastCabinet,
} from 'containers/Configurator/selectors/selection';
import { makeSelectCabinet } from 'containers/Configurator/selectors/furniture';
import { shelvesSelectionAction } from 'containers/Configurator/actions/selection';
import { selectionIntermediateShelvesAction } from 'containers/IntermediateShelfPanel/actions';
import { makeSelectIsSameIntermediateShelvesInShelvesSelected } from 'containers/IntermediateShelfPanel/selectors';
import ActionTypesSelection from 'containers/Configurator/constants/selection';
import ActionTypesHeader from 'containers/Header/constants';
import { setShelfModeAction } from 'containers/Configurator/actions/general';
import { makeSelectActiveTab } from 'containers/Header/selectors';
import TabNames from 'containers/Header/TabNames';
import { openDoorsAction } from 'containers/Viewer/actions';
import { makeSelectOpenDoorsMode } from '../selectors/general';
import { downloadColorsFromAPI } from './colors';
import i18n from '../../../i18n';
import { downloadRulesFromAPI } from './rules';
import { switchTabAction } from '../../Header/actions';
import { makeSelectIsDeleteMode } from '../../ModeBar/selectors';

export function* openColorsPanelSaga() {
  while (true) {
    const { payload } = yield take(ActionTypesGeneral.SET_COLORS_PANEL);
    const openDoorsMode: boolean = yield select(makeSelectOpenDoorsMode());
    const activeTab: TabNames = yield select(makeSelectActiveTab());
    const deleteMode: boolean = yield select(makeSelectIsDeleteMode());

    if (!payload.openDefault) continue;

    // Avoid shelves selection if we paint in cabinet mode
    if (
      (payload.value === ActivePanel.paint || deleteMode) &&
      activeTab === TabNames.cabinet
    ) {
      continue;
    }

    let shelvesLevelSelected: number[] = yield select(
      makeSelectShelvesLevelSelected(),
    );

    // Make the reselection if necessary, base on the open panel
    // Example: We select only the drawers shelves in a cabinet with
    // others shelves were also previously selected if we open the drawers colors panel

    yield put(shelvesSelectionAction(shelvesLevelSelected));
    shelvesLevelSelected = yield select(makeSelectShelvesLevelSelected());

    const cabinet: Cabinet = yield select(makeSelectCabinet());
    if (!cabinet) continue;

    let selectedShelves: Shelf[] = [];
    if (shelvesLevelSelected.length === 0) selectedShelves = cabinet.shelves;
    else
      selectedShelves = cabinet.shelves.filter(item =>
        shelvesLevelSelected.includes(item.level),
      );
    if (payload.value === ActivePanel.IntermediateShelf) {
      // for (let shelf of selectedShelves) {
      //   // shelf.prepareIntermediateShelf();
      // }
      const sameIS = yield select(
        makeSelectIsSameIntermediateShelvesInShelvesSelected(),
      );
      if (sameIS) {
        yield put(selectionIntermediateShelvesAction([0]));
      }
      yield put(
        openDoorsAction(100, [
          PositionAngle.FrontRight,
          PositionAngle.BackLeft,
        ]),
      );
    } else if (payload.value === ActivePanel.plates) {
      yield put(
        openDoorsAction(100, [
          PositionAngle.FrontRight,
          PositionAngle.BackLeft,
        ]),
      );
    } else if (payload.value === ActivePanel.paint) {
      // yield put(shelvesSelectionAction([]));
      yield put(switchTabAction(TabNames.cabinet));
    } else if (payload.value === ActivePanel.handles) {
      const compositions = yield select(makeSelectGetSetCompositionSelected());
      const numberDrawers = yield select(makeSelectDrawerSelectedNumber());
      if (compositions.size === 1) {
        if (
          compositions.has(ShelfComposition.TwoDoors) ||
          compositions.has(ShelfComposition.LargeTwoDoors)
        ) {
          yield put(
            selectionHandlesAction([
              PositionAngle.FrontLeft,
              PositionAngle.FrontRight,
            ]),
          );
        } else if (
          compositions.has(ShelfComposition.FourDoors) ||
          compositions.has(ShelfComposition.LargeFourDoors)
        ) {
          yield put(
            selectionHandlesAction([
              PositionAngle.FrontLeft,
              PositionAngle.FrontRight,
              PositionAngle.BackLeft,
              PositionAngle.BackRight,
            ]),
          );
        } else if (compositions.has(ShelfComposition.Drawer)) {
          if (numberDrawers) {
            yield put(selectionHandlesAction(_.range(numberDrawers)));
          } else {
            // The number of drawers are not the same for every shelves
            yield put(selectionHandlesAction(['Drawers']));
          }
        }
      } else {
        if (
          compositions.has(ShelfComposition.TwoDoors) ||
          compositions.has(ShelfComposition.LargeTwoDoors) ||
          compositions.has(ShelfComposition.FourDoors) ||
          compositions.has(ShelfComposition.LargeFourDoors)
        ) {
          yield put(selectionHandlesAction(['Doors']));
        } else if (compositions.has(ShelfComposition.Drawer)) {
          yield put(selectionHandlesAction(['Drawers']));
        } else {
          yield put(selectionHandlesAction(['Slats']));
        }
      }
    } else if (payload.value === ActivePanel.crossPieces) {
      // The selection must have one shelf maximum for edit cross piece
      const shelvesInCabinet: Shelf[] = yield select(
        makeSelectShelvesSelectedLastCabinet(),
      );
      if (shelvesLevelSelected.length > 1) {
        yield put(shelvesSelectionAction([shelvesLevelSelected[0]]));
      } else if (
        shelvesLevelSelected.length === 0 &&
        shelvesInCabinet.length > 1
      ) {
        yield put(shelvesSelectionAction([0]));
      }
    } else if (typeof payload.value === 'undefined') {
      const shelfSelection = yield select(makeSelectShelfSelectionProfile());
      shelfSelection.unselectAll();
      yield put(updateProfileSelectionAction(shelfSelection));
      yield put(selectionHandlesAction([]));
      if (!openDoorsMode) {
        yield put(
          openDoorsAction(0, [
            PositionAngle.FrontRight,
            PositionAngle.FrontLeft,
            PositionAngle.BackRight,
            PositionAngle.BackLeft,
          ]),
        );
      }
    }
  }
}

export function* shelfModeSaga() {
  while (true) {
    const winner = yield race({
      selection: take(ActionTypesSelection.ADD_TO_SHELVES_SELECTION),
      selectionMultiple: take(ActionTypesSelection.SHELVES_SELECTION),
      switchTab: take(ActionTypesHeader.SWITCH_TAB_REQUEST),
    });

    let activeTab: TabNames;

    if (winner.switchTab) {
      activeTab = winner.switchTab.payload.tab;
    } else {
      activeTab = yield select(makeSelectActiveTab());
    }

    if (activeTab === TabNames.shelves) {
      yield put(setShelfModeAction(true));
    } else {
      yield put(setShelfModeAction(false));
    }
  }
}

export function* shelfOpenDoorsModeSaga() {
  while (true) {
    const { payload } = yield take(ActionTypesGeneral.OPEN_DOORS_MODE);

    if (payload.value) {
      yield put(openDoorsAction(100));
    } else {
      yield put(openDoorsAction(0));
    }
  }
}

export function* languageSaga() {
  while (true) {
    const { payload } = yield take(ActionTypesGeneral.SWITCH_LANGUAGE);
    const { code } = payload;

    i18n.changeLanguage(code);
    yield call(downloadColorsFromAPI);
    yield call(downloadRulesFromAPI);
  }
}
